import type { IntlShape } from 'react-intl'

import { isAfter, isBefore, isValid, parseISO, startOfDay, subDays, subMonths, subYears } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { isUnclosedSessionStatus } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/constants'
import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { startCaseDelimitedString } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { SESSION_TYPE_MESSAGES } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/messages'
import { getSessionKindFromType } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Episode, EpisodeStates } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { Note, NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { CareTimelineSession } from './data/careTimelineApi'
import { getGeneralNoteTitle } from '../clientNotes/utils/noteTitleUtils'
import { isIntakeNote } from '../clientNotes/utils/npSessionNote'

export enum CareTimelineItemType {
  SESSION = 'session',
  CARE_REFERRAL = 'careReferral',
  GENERAL_NOTE = 'generalNote',
  MILESTONE = 'milestone',
}

export enum CareTimelineMilestoneType {
  CARE_STARTED = 'careStarted',
  CARE_ENDED = 'careEnded',
  NEVER_ENGAGED = 'neverEngaged',
}

export enum CareTimelineAttendance {
  ATTENDED = 'attended',
  UPCOMING = 'upcoming',
  CANCELLED = 'cancelled',
  MISSED = 'missed',
}

export enum CareTimelineSort {
  NEWEST = 'newest',
  OLDEST = 'oldest',
}

export enum CareTimelineDateFilter {
  ANY = 'any',
  PAST_WEEK = 'pastWeek',
  PAST_MONTH = 'pastMonth',
  PAST_YEAR = 'pastYear',
  CUSTOM_RANGE = 'customRange',
}

export enum CareTimelineTypeFilter {
  CARE_START_STOP = 'careStartStop',
  GENERAL_NOTES = 'generalNotes',
  REFERRALS = 'referrals',
  SESSIONS = 'sessions',
}

export type CareTimelineItem = {
  id: string
  date: Date
  appointmentId?: number
  appointmentStatus?: Appointment['appointmentStatus']
  durationMinutes?: number
  episodeId: string
  title: string
  hasExplicitTitle?: boolean
  detailType: string
  itemType: CareTimelineItemType
  programName?: string
  providerId?: string
  providerImageUrl?: string
  providerName: string
  serviceType: string
  attendance?: CareTimelineAttendance
  cancelName?: string
  sessionNumber?: number | null
  sessionType?: string | null
  suggestedSessionType?: string | null
  teenName?: string
  caregiverName?: string
  hasNote?: boolean
  noteId?: string
  noteActivityDate?: Date
  noteStatus?: NoteStatusStates
  isOpenSession?: boolean
  isChargeReversed?: boolean
  isLegacyDAAppointment?: boolean
  isPastSession?: boolean
  recordingId?: string
  recordingAvailable?: boolean
  isCancelled: boolean
  milestoneType?: CareTimelineMilestoneType
  dischargeReason?: EpisodeStates
  dischargeComment?: string | null
  noteLoadFailed?: boolean
}

export type CareTimelineFilters = {
  sort: CareTimelineSort
  service: string[] | 'all'
  provider: string[] | 'all'
  attendance: CareTimelineAttendance[]
  type: CareTimelineTypeFilter[]
  date: CareTimelineDateFilter
  customDateFrom: string
  customDateTo: string
}

export type CareTimelineDisplayRow = {
  item: CareTimelineItem
  showDate: boolean
  hasFollowingItemOnSameDay: boolean
}

type GetCareTimelineDefaultScopeArgs = {
  episodes: Episode[]
  selectedEpisodeId?: string
  serviceNameByEpisodeId: Record<string, string>
  userId: string
}

export const getCareTimelineDefaultScope = ({
  episodes,
  selectedEpisodeId,
  serviceNameByEpisodeId,
  userId,
}: GetCareTimelineDefaultScopeArgs): { provider: string; service: string } => {
  const providerEpisodes = episodes.filter((episode) => episode.provider_id === userId)
  const careEpisode =
    providerEpisodes.find((episode) => episode.id === selectedEpisodeId) ||
    providerEpisodes.find((episode) => episode.state === 'in_progress') ||
    providerEpisodes[0]
  const providerServices = Array.from(
    new Set(providerEpisodes.map((episode) => serviceNameByEpisodeId[episode.id]).filter(Boolean)),
  )

  return {
    provider: careEpisode ? userId : 'all',
    service: careEpisode ? serviceNameByEpisodeId[careEpisode.id] || providerServices[0] || 'all' : 'all',
  }
}

type BuildCareTimelineItemsArgs = {
  appointments: Appointment[]
  notes: Note[]
  episodes: Episode[]
  intl: IntlShape
  providerNameByEpisodeId?: Record<string, string>
  recordingIdByAppointmentId?: Record<number, string>
  serviceNameByEpisodeId?: Record<string, string>
  currentDate?: Date
  failedNoteEpisodeIds?: Set<string>
}

const terminalMilestoneByEpisodeState: Partial<Record<EpisodeStates, CareTimelineMilestoneType>> = {
  [EpisodeStates.BOOSTER]: CareTimelineMilestoneType.CARE_ENDED,
  [EpisodeStates.CARE_TRANSITION]: CareTimelineMilestoneType.CARE_ENDED,
  [EpisodeStates.COMPLETED]: CareTimelineMilestoneType.CARE_ENDED,
  [EpisodeStates.DROP_OUT]: CareTimelineMilestoneType.CARE_ENDED,
  [EpisodeStates.NOT_ELIGIBLE]: CareTimelineMilestoneType.CARE_ENDED,
  [EpisodeStates.NOT_INITIATED]: CareTimelineMilestoneType.NEVER_ENGAGED,
  [EpisodeStates.NOT_SUPPORTED]: CareTimelineMilestoneType.CARE_ENDED,
}

const getProviderName = (appointment: Appointment, providerNameByEpisodeId: Record<string, string>): string => {
  const provider = appointment.provider
  if (provider?.display_name) {
    return provider.display_name
  }

  const name = [provider?.display_first_name || provider?.first_name, provider?.last_name].filter(Boolean).join(' ')
  return name || providerNameByEpisodeId[appointment.episodeId ?? ''] || '—'
}

const getCancelName = (
  changeReason: string | null | undefined,
  providerName: string,
  clientName: string | undefined,
): string | undefined => {
  if (changeReason === 'providerInitiated') {
    return providerName !== '—' ? providerName : undefined
  }
  if (['clientCancel', 'clientInitiated', 'clientInitiatedLate', 'clientTransition'].includes(changeReason ?? '')) {
    return clientName
  }
  return undefined
}

export const formatCareTimelineValue = (value?: string | null): string => {
  if (!value) {
    return '—'
  }

  return startCaseDelimitedString(value, ' ', '_')
}

const getCareTimelineMilestoneTitle = (intl: IntlShape, milestoneType: CareTimelineMilestoneType): string => {
  switch (milestoneType) {
    case CareTimelineMilestoneType.CARE_ENDED:
      return intl.formatMessage({
        defaultMessage: 'Ended care',
        description: 'Title for a Care Timeline milestone when a client ends care',
      })
    case CareTimelineMilestoneType.NEVER_ENGAGED:
      return intl.formatMessage({
        defaultMessage: 'Never engaged',
        description: 'Title for a Care Timeline milestone when a client never engages in care',
      })
    case CareTimelineMilestoneType.CARE_STARTED:
    default:
      return intl.formatMessage({
        defaultMessage: 'Care started',
        description: 'Title for a Care Timeline milestone when a client starts care',
      })
  }
}

export const getCareTimelineMilestoneDescription = (
  intl: IntlShape,
  clientFirstName: string,
  item: CareTimelineItem,
): string => {
  switch (item.milestoneType) {
    case CareTimelineMilestoneType.CARE_ENDED:
      return intl.formatMessage(
        {
          defaultMessage: '{clientFirstName} ended care with {serviceType}',
          description: 'Care Timeline milestone description when a client ends care',
        },
        { clientFirstName, serviceType: item.serviceType },
      )
    case CareTimelineMilestoneType.NEVER_ENGAGED:
      return intl.formatMessage(
        {
          defaultMessage: '{clientFirstName} never engaged in {serviceType}',
          description: 'Care Timeline milestone description when a client never engages in care',
        },
        { clientFirstName, serviceType: item.serviceType },
      )
    case CareTimelineMilestoneType.CARE_STARTED:
    default:
      return intl.formatMessage(
        {
          defaultMessage: '{clientFirstName} started {serviceType}',
          description: 'Care Timeline milestone description when a client starts care',
        },
        { clientFirstName, serviceType: item.serviceType },
      )
  }
}

export const formatMeetingFormat = (intl: IntlShape, meetingFormat: MEETING_FORMATS): string => {
  const message = SESSION_TYPE_MESSAGES[meetingFormat]
  return message ? intl.formatMessage(message) : formatCareTimelineValue(meetingFormat)
}

export const getCareTimelineAttendance = (
  appointment: Appointment,
  currentDate = new Date(),
): CareTimelineAttendance => {
  if (appointment.appointmentStatus === 'canceled') {
    return CareTimelineAttendance.CANCELLED
  }
  if (appointment.appointmentStatus === 'missed') {
    return CareTimelineAttendance.MISSED
  }
  if (getDateFromAppointment(appointment) > currentDate) {
    return CareTimelineAttendance.UPCOMING
  }
  return CareTimelineAttendance.ATTENDED
}

const getNoteDate = (note: Note): Date =>
  new Date(note.submit_date || note.update_date || note.create_date || note._create_date)

const getNoteActivityDate = (note: Note): Date | undefined => {
  const timestamp =
    note.status === NoteStatusStates.DRAFT
      ? note.update_date || note._update_date || note.create_date || note._create_date
      : note.submit_date || note.update_date || note._update_date || note.create_date || note._create_date
  const activityDate = timestamp ? new Date(timestamp) : undefined

  return activityDate && isValid(activityDate) ? activityDate : undefined
}

// suggestedSessionType is backend-computed for retroactive appointments where sessionType is not
// yet set; it takes precedence over the sessionNumber heuristic below, matching getLTSessionTypeAndRate.
const resolveGenericSessionTitleKind = ({
  sessionType,
  suggestedSessionType,
  sessionNumber,
}: {
  sessionType?: string | null
  suggestedSessionType?: string | null
  sessionNumber?: number | null
}): 'intake' | 'followup' | 'generic' => {
  const kind = getSessionKindFromType({ sessionType, suggestedSessionType })
  if (kind) {
    return kind
  }
  if (sessionNumber === 1) {
    return 'intake'
  }
  if (sessionNumber && sessionNumber > 1) {
    return 'followup'
  }
  return 'generic'
}

const getGenericSessionTitle = (
  intl: IntlShape,
  args: { sessionType?: string | null; suggestedSessionType?: string | null; sessionNumber?: number | null },
): string => {
  const kind = resolveGenericSessionTitleKind(args)
  if (kind === 'intake') {
    return intl.formatMessage({
      defaultMessage: 'Intake session',
      description: 'Care Timeline title for an intake/initial session',
    })
  }
  if (kind === 'followup') {
    return intl.formatMessage({
      defaultMessage: 'Follow-up session',
      description: 'Care Timeline title for a follow-up session',
    })
  }
  return intl.formatMessage({
    defaultMessage: 'Session',
    description: 'Fallback Care Timeline session title',
  })
}

export const buildCareTimelineItems = ({
  appointments,
  notes,
  episodes,
  intl,
  providerNameByEpisodeId = {},
  recordingIdByAppointmentId = {},
  serviceNameByEpisodeId = {},
  currentDate = new Date(),
  failedNoteEpisodeIds = new Set<string>(),
}: BuildCareTimelineItemsArgs): CareTimelineItem[] => {
  const episodeById = new Map(episodes.map((episode) => [episode.id, episode]))
  const appointmentByProviderId = new Map<string, Appointment>()
  appointments.forEach((appointment) => {
    const providerId = appointment.provider?.lyra_id || appointment.lyraProviderId
    if (!providerId) {
      return
    }

    const existingAppointment = appointmentByProviderId.get(providerId)
    const hasProviderProfile = appointment.image != null || appointment.provider != null
    const existingHasProviderProfile = existingAppointment?.image != null || existingAppointment?.provider != null
    if (!existingAppointment || (hasProviderProfile && !existingHasProviderProfile)) {
      appointmentByProviderId.set(providerId, appointment)
    }
  })
  const appointmentNoteById = new Map(
    notes
      .filter((note) => note.appointment_id != null && [NoteTypes.INITIAL, NoteTypes.SESSION].includes(note.note_type))
      .map((note) => [note.appointment_id, note]),
  )
  const intakeNoteByEpisodeId = new Map(
    notes
      .filter((note) => isIntakeNote(note) && note.status === NoteStatusStates.COMPLETED)
      .map((note) => [note.episode_id, note]),
  )
  const earliestAttendedAppointmentDateByEpisodeId = new Map<string, Date>()
  appointments.forEach((appointment) => {
    if (!appointment.episodeId || ['canceled', 'missed'].includes(appointment.appointmentStatus)) {
      return
    }

    const appointmentDate = getDateFromAppointment(appointment)
    if (!isValid(appointmentDate) || appointmentDate > currentDate) {
      return
    }

    const earliestAppointmentDate = earliestAttendedAppointmentDateByEpisodeId.get(appointment.episodeId)
    if (!earliestAppointmentDate || appointmentDate < earliestAppointmentDate) {
      earliestAttendedAppointmentDateByEpisodeId.set(appointment.episodeId, appointmentDate)
    }
  })

  const childNameByEpisodeId = new Map<string, string>()
  const guardianNameByEpisodeId = new Map<string, string>()
  const userFirstNameByEpisodeId = new Map<string, string>()
  for (const a of appointments) {
    const eid = a.episodeId ?? ''
    if (episodeById.get(eid)?.program_name !== ProgramNames.DialecticalBehaviorTherapyTeens) continue
    if (a.childFirstName && !childNameByEpisodeId.has(eid)) {
      childNameByEpisodeId.set(eid, a.childFirstName)
    }
    if (a.guardianFirstName && !guardianNameByEpisodeId.has(eid)) {
      guardianNameByEpisodeId.set(eid, a.guardianFirstName)
    }
    if (a.userInfo?.firstName && !userFirstNameByEpisodeId.has(eid)) {
      userFirstNameByEpisodeId.set(eid, a.userInfo.firstName)
    }
  }

  const sessionItems = appointments.map<CareTimelineItem>((appointment) => {
    const episodeId = appointment.episodeId ?? ''
    const episode = episodeById.get(episodeId)
    const attendance = getCareTimelineAttendance(appointment, currentDate)
    const note = appointmentNoteById.get(appointment.appointmentId)
    const sessionDate = getDateFromAppointment(appointment)

    const childFirstName = childNameByEpisodeId.get(episodeId)
    const guardianFirstName = guardianNameByEpisodeId.get(episodeId)
    const userFirstName = userFirstNameByEpisodeId.get(episodeId)
    const resolvedTeenName =
      childFirstName || (guardianFirstName && guardianFirstName !== userFirstName ? userFirstName : undefined)
    const resolvedCaregiverName =
      guardianFirstName || (childFirstName && childFirstName !== userFirstName ? userFirstName : undefined)

    return {
      id: `session-${appointment.appointmentId}`,
      date: sessionDate,
      appointmentId: appointment.appointmentId,
      appointmentStatus: appointment.appointmentStatus,
      durationMinutes: appointment.appointmentDuration,
      episodeId,
      title:
        appointment.title ||
        getGenericSessionTitle(intl, {
          sessionType: appointment.sessionType,
          suggestedSessionType: appointment.suggestedSessionType,
          sessionNumber: appointment.sessionNumber,
        }),
      hasExplicitTitle: Boolean(appointment.title),
      detailType: formatMeetingFormat(intl, appointment.meetingFormat),
      itemType: CareTimelineItemType.SESSION,
      programName: episode?.program_name,
      providerId: appointment.provider?.lyra_id || appointment.lyraProviderId || episode?.provider_id,
      providerImageUrl: appointment.image?.portraitImageUrl || appointment.image?.imageUrl,
      providerName: getProviderName(appointment, providerNameByEpisodeId),
      serviceType:
        serviceNameByEpisodeId[episodeId] ||
        formatCareTimelineValue(episode?.program_name || appointment.treatmentType),
      attendance,
      cancelName:
        attendance === CareTimelineAttendance.CANCELLED
          ? getCancelName(
              appointment.changeReason,
              getProviderName(appointment, providerNameByEpisodeId),
              appointment.userInfo?.firstName,
            )
          : undefined,
      sessionNumber: appointment.sessionNumber,
      sessionType: appointment.sessionType,
      suggestedSessionType: appointment.suggestedSessionType,
      teenName: resolvedTeenName,
      caregiverName: resolvedCaregiverName,
      hasNote: Boolean(note),
      noteId: note?.id,
      noteActivityDate: note ? getNoteActivityDate(note) : undefined,
      noteStatus: note?.status,
      isOpenSession: isUnclosedSessionStatus(appointment.appointmentStatus) && sessionDate <= currentDate,
      isChargeReversed: Boolean(appointment.isChargeReversed),
      isLegacyDAAppointment: appointment.programId == null,
      isPastSession: sessionDate <= currentDate,
      recordingId: recordingIdByAppointmentId[appointment.appointmentId] || appointment.videoRecording,
      recordingAvailable: Boolean(recordingIdByAppointmentId[appointment.appointmentId] || appointment.videoRecording),
      isCancelled: [CareTimelineAttendance.CANCELLED, CareTimelineAttendance.MISSED].includes(attendance),
      noteLoadFailed:
        !note &&
        failedNoteEpisodeIds.has(episodeId) &&
        sessionDate <= currentDate &&
        ![CareTimelineAttendance.CANCELLED, CareTimelineAttendance.MISSED].includes(attendance),
    }
  })

  const noteItems = notes.flatMap<CareTimelineItem>((note) => {
    if (
      note.note_type !== NoteTypes.GENERAL &&
      note.note_type !== NoteTypes.REFERRAL &&
      note.note_type !== NoteTypes.DISCHARGE
    ) {
      return []
    }

    const isGeneralNote = note.note_type === NoteTypes.GENERAL
    const isDischargeNote = note.note_type === NoteTypes.DISCHARGE
    const noteTitle = getGeneralNoteTitle(note)
    const episode = episodeById.get(note.episode_id)
    const authorName = typeof note.body.authorName === 'string' ? note.body.authorName.trim() : ''
    const authorLyraId = note.body.authorLyraId
    const authorProviderId =
      (typeof authorLyraId === 'string' || typeof authorLyraId === 'number' ? String(authorLyraId).trim() : '') ||
      note.provider_id
    const providerAppointment = appointmentByProviderId.get(authorProviderId)
    const providerName =
      (providerAppointment ? getProviderName(providerAppointment, providerNameByEpisodeId) : '') ||
      (episode?.provider_id === authorProviderId ? providerNameByEpisodeId[note.episode_id] : '') ||
      authorName ||
      '—'

    return [
      {
        id: `note-${note.id}`,
        date: getNoteDate(note),
        episodeId: note.episode_id,
        title: isDischargeNote
          ? intl.formatMessage({
              defaultMessage: 'Discharge note',
              description: 'Title for a discharge note in Care Timeline',
            })
          : isGeneralNote
            ? noteTitle ||
              intl.formatMessage({
                defaultMessage: 'General note',
                description: 'Fallback title for a general note in Care Timeline',
              })
            : intl.formatMessage({
                defaultMessage: 'Care referral',
                description: 'Title for a care referral in Care Timeline',
              }),
        detailType: isDischargeNote
          ? intl.formatMessage({
              defaultMessage: 'Discharge note',
              description: 'Item type for a discharge note in Care Timeline',
            })
          : isGeneralNote
            ? intl.formatMessage({
                defaultMessage: 'General note',
                description: 'Item type for a general note in Care Timeline',
              })
            : intl.formatMessage({
                defaultMessage: 'Care referral',
                description: 'Item type for a care referral in Care Timeline',
              }),
        itemType:
          isGeneralNote || isDischargeNote ? CareTimelineItemType.GENERAL_NOTE : CareTimelineItemType.CARE_REFERRAL,
        programName: episode?.program_name,
        providerId: authorProviderId,
        providerImageUrl: providerAppointment?.image?.portraitImageUrl || providerAppointment?.image?.imageUrl,
        providerName,
        serviceType: serviceNameByEpisodeId[note.episode_id] || formatCareTimelineValue(episode?.program_name),
        hasNote: true,
        noteId: note.id,
        noteActivityDate: getNoteActivityDate(note),
        noteStatus: note.status,
        isCancelled: false,
      },
    ]
  })

  const milestoneItems = episodes.flatMap<CareTimelineItem>((episode) => {
    const serviceType = serviceNameByEpisodeId[episode.id] || formatCareTimelineValue(episode.program_name)
    const providerName = providerNameByEpisodeId[episode.id] || '—'
    const episodeStartDate = episode.start_date ? parseISO(episode.start_date) : undefined
    const earliestAppointmentDate = earliestAttendedAppointmentDateByEpisodeId.get(episode.id)
    const startDate =
      episodeStartDate && isValid(episodeStartDate)
        ? earliestAppointmentDate && earliestAppointmentDate < episodeStartDate
          ? earliestAppointmentDate
          : episodeStartDate
        : earliestAppointmentDate
    const intakeNote = intakeNoteByEpisodeId.get(episode.id)
    const milestones: CareTimelineItem[] = []

    if (startDate && isValid(startDate)) {
      milestones.push({
        id: `milestone-${episode.id}-care-started`,
        date: startDate,
        episodeId: episode.id,
        title: getCareTimelineMilestoneTitle(intl, CareTimelineMilestoneType.CARE_STARTED),
        detailType: intl.formatMessage({
          defaultMessage: 'Milestone',
          description: 'Item type for a milestone in Care Timeline',
        }),
        itemType: CareTimelineItemType.MILESTONE,
        milestoneType: CareTimelineMilestoneType.CARE_STARTED,
        programName: episode.program_name,
        providerId: episode.provider_id,
        providerName,
        serviceType,
        hasNote: Boolean(intakeNote),
        noteId: intakeNote?.id,
        isCancelled: false,
      })
    }

    const terminalMilestoneType = terminalMilestoneByEpisodeState[episode.state]
    if (terminalMilestoneType && episode.end_date) {
      const endDate = parseISO(episode.end_date)
      if (isValid(endDate)) {
        milestones.push({
          id: `milestone-${episode.id}-${terminalMilestoneType}`,
          date: endDate,
          episodeId: episode.id,
          title: getCareTimelineMilestoneTitle(intl, terminalMilestoneType),
          detailType: intl.formatMessage({
            defaultMessage: 'Milestone',
            description: 'Item type for a milestone in Care Timeline',
          }),
          itemType: CareTimelineItemType.MILESTONE,
          milestoneType: terminalMilestoneType,
          dischargeReason: episode.state,
          dischargeComment: episode.end_episode_note,
          programName: episode.program_name,
          providerId: episode.provider_id,
          providerName,
          serviceType,
          isCancelled: false,
        })
      }
    }

    return milestones
  })

  return [...sessionItems, ...noteItems, ...milestoneItems]
}

const getAttendanceFromStatus = (status: string, sessionDate: Date, currentDate: Date): CareTimelineAttendance => {
  if (status === 'canceled') {
    return CareTimelineAttendance.CANCELLED
  }
  if (status === 'missed') {
    return CareTimelineAttendance.MISSED
  }
  if (sessionDate > currentDate) {
    return CareTimelineAttendance.UPCOMING
  }
  return CareTimelineAttendance.ATTENDED
}

type BuildCareTimelineSessionItemsArgs = {
  sessions: CareTimelineSession[]
  notes: Note[]
  episodes: Episode[]
  intl: IntlShape
  clientFirstName?: string
  existingAppointments?: Appointment[]
  recordingIdByAppointmentId?: Record<number, string>
  serviceNameByEpisodeId?: Record<string, string>
  currentDate?: Date
  failedNoteEpisodeIds?: Set<string>
}

export const buildCareTimelineSessionItems = ({
  sessions,
  notes,
  episodes,
  intl,
  clientFirstName,
  existingAppointments = [],
  recordingIdByAppointmentId = {},
  serviceNameByEpisodeId = {},
  currentDate = new Date(),
  failedNoteEpisodeIds = new Set<string>(),
}: BuildCareTimelineSessionItemsArgs): CareTimelineItem[] => {
  const appointmentNoteById = new Map(
    notes
      .filter((note) => note.appointment_id != null && [NoteTypes.INITIAL, NoteTypes.SESSION].includes(note.note_type))
      .map((note) => [note.appointment_id, note]),
  )

  const existingAppointmentById = new Map(
    existingAppointments.map((appointment) => [appointment.appointmentId, appointment]),
  )

  const episodeById = new Map(episodes.map((episode) => [episode.id, episode]))

  const earliestAttendedDateByEpisodeId = new Map<string, Date>()
  sessions.forEach((session) => {
    if (['canceled', 'missed'].includes(session.appointmentStatus)) {
      return
    }
    const sessionDate = parseISO(session.startDateTime)
    if (!isValid(sessionDate) || sessionDate > currentDate) {
      return
    }
    const earliest = earliestAttendedDateByEpisodeId.get(session.episodeId)
    if (!earliest || sessionDate < earliest) {
      earliestAttendedDateByEpisodeId.set(session.episodeId, sessionDate)
    }
  })

  const intakeNoteByEpisodeId = new Map(
    notes
      .filter((note) => isIntakeNote(note) && note.status === NoteStatusStates.COMPLETED)
      .map((note) => [note.episode_id, note]),
  )

  const isDBTTeensEpisode = (episodeId: string) =>
    episodeById.get(episodeId)?.program_name === ProgramNames.DialecticalBehaviorTherapyTeens

  const childNameByEpisodeId = new Map<string, string>()
  const guardianNameByEpisodeId = new Map<string, string>()
  for (const s of sessions) {
    if (!isDBTTeensEpisode(s.episodeId)) continue
    if (s.childFirstName && !childNameByEpisodeId.has(s.episodeId)) {
      childNameByEpisodeId.set(s.episodeId, s.childFirstName)
    }
    if (s.guardianFirstName && !guardianNameByEpisodeId.has(s.episodeId)) {
      guardianNameByEpisodeId.set(s.episodeId, s.guardianFirstName)
    }
  }
  for (const a of existingAppointments) {
    const eid = a.episodeId ?? ''
    if (!isDBTTeensEpisode(eid)) continue
    if (a.childFirstName && !childNameByEpisodeId.has(eid)) {
      childNameByEpisodeId.set(eid, a.childFirstName)
    }
    if (a.guardianFirstName && !guardianNameByEpisodeId.has(eid)) {
      guardianNameByEpisodeId.set(eid, a.guardianFirstName)
    }
  }

  const sessionItems = sessions.map<CareTimelineItem>((session) => {
    const appointmentId = Number(session.appointmentId)
    const sessionDate = parseISO(session.startDateTime)
    const attendance = getAttendanceFromStatus(session.appointmentStatus, sessionDate, currentDate)
    const note = appointmentNoteById.get(appointmentId)
    const existingAppointment = existingAppointmentById.get(appointmentId)
    const episode = episodeById.get(session.episodeId)

    const childFirstName = childNameByEpisodeId.get(session.episodeId)
    const guardianFirstName = guardianNameByEpisodeId.get(session.episodeId)
    const teenName =
      childFirstName || (guardianFirstName && guardianFirstName !== clientFirstName ? clientFirstName : undefined)
    const caregiverName =
      guardianFirstName || (childFirstName && childFirstName !== clientFirstName ? clientFirstName : undefined)
    const providerName = session.provider.displayName || session.provider.name || '—'
    const cancelledBy = session.cancelledByLyraId
    const clientCancelName = isDBTTeensEpisode(session.episodeId)
      ? (cancelledBy && cancelledBy === session.guardianLyraId && session.guardianFirstName) ||
        (cancelledBy && cancelledBy === session.childLyraId && session.childFirstName) ||
        undefined
      : existingAppointment?.userInfo?.firstName || clientFirstName

    return {
      id: `session-${session.appointmentId}`,
      date: sessionDate,
      appointmentId,
      appointmentStatus: session.appointmentStatus as Appointment['appointmentStatus'],
      durationMinutes: session.duration,
      episodeId: session.episodeId,
      title: getGenericSessionTitle(intl, {
        sessionType: session.sessionType,
        suggestedSessionType: session.suggestedSessionType,
        sessionNumber: session.sessionNumber,
      }),
      detailType: formatMeetingFormat(intl, session.meetingFormat as MEETING_FORMATS),
      itemType: CareTimelineItemType.SESSION,
      programName: session.programName || episode?.program_name,
      providerId: session.provider.id ?? episode?.provider_id,
      providerImageUrl: session.provider.portraitImageUrl || session.provider.imageUrl || undefined,
      providerName,
      serviceType:
        serviceNameByEpisodeId[session.episodeId] ||
        formatCareTimelineValue(session.programName || episode?.program_name),
      attendance,
      cancelName:
        attendance === CareTimelineAttendance.CANCELLED
          ? getCancelName(session.changeReason ?? existingAppointment?.changeReason, providerName, clientCancelName)
          : undefined,
      sessionNumber: session.sessionNumber,
      sessionType: session.sessionType,
      suggestedSessionType: session.suggestedSessionType,
      teenName,
      caregiverName,
      hasNote: Boolean(note),
      noteId: note?.id,
      noteActivityDate: note ? getNoteActivityDate(note) : undefined,
      noteStatus: note?.status,
      isOpenSession: isUnclosedSessionStatus(session.appointmentStatus) && sessionDate <= currentDate,
      isChargeReversed: Boolean(existingAppointment?.isChargeReversed),
      isLegacyDAAppointment: session.programId == null,
      isPastSession: sessionDate <= currentDate,
      recordingId: recordingIdByAppointmentId[appointmentId] || existingAppointment?.videoRecording,
      recordingAvailable: Boolean(recordingIdByAppointmentId[appointmentId] || existingAppointment?.videoRecording),
      isCancelled: [CareTimelineAttendance.CANCELLED, CareTimelineAttendance.MISSED].includes(attendance),
      noteLoadFailed:
        !note &&
        failedNoteEpisodeIds.has(session.episodeId) &&
        sessionDate <= currentDate &&
        ![CareTimelineAttendance.CANCELLED, CareTimelineAttendance.MISSED].includes(attendance),
    }
  })

  const providerNameByProviderId = new Map(
    sessions
      .filter((session) => session.provider.id != null)
      .map((session) => [session.provider.id, session.provider.displayName || session.provider.name]),
  )

  const providerImageByProviderId = new Map(
    sessions
      .filter(
        (session) => session.provider.id != null && (session.provider.portraitImageUrl || session.provider.imageUrl),
      )
      .map((session) => [session.provider.id, session.provider.portraitImageUrl || session.provider.imageUrl]),
  )

  const noteItems = notes.flatMap<CareTimelineItem>((note) => {
    if (
      note.note_type !== NoteTypes.GENERAL &&
      note.note_type !== NoteTypes.REFERRAL &&
      note.note_type !== NoteTypes.DISCHARGE
    ) {
      return []
    }

    const isGeneralNote = note.note_type === NoteTypes.GENERAL
    const isDischargeNote = note.note_type === NoteTypes.DISCHARGE
    const noteTitle = getGeneralNoteTitle(note)
    const episode = episodeById.get(note.episode_id)
    const authorName = typeof note.body.authorName === 'string' ? note.body.authorName.trim() : ''
    const authorLyraId = note.body.authorLyraId
    const authorProviderId =
      (typeof authorLyraId === 'string' || typeof authorLyraId === 'number' ? String(authorLyraId).trim() : '') ||
      note.provider_id
    const providerName = providerNameByProviderId.get(authorProviderId) || authorName || '—'

    return [
      {
        id: `note-${note.id}`,
        date: getNoteDate(note),
        episodeId: note.episode_id,
        title: isDischargeNote
          ? intl.formatMessage({
              defaultMessage: 'Discharge note',
              description: 'Title for a discharge note in Care Timeline',
            })
          : isGeneralNote
            ? noteTitle ||
              intl.formatMessage({
                defaultMessage: 'General note',
                description: 'Fallback title for a general note in Care Timeline',
              })
            : intl.formatMessage({
                defaultMessage: 'Care referral',
                description: 'Title for a care referral in Care Timeline',
              }),
        detailType: isDischargeNote
          ? intl.formatMessage({
              defaultMessage: 'Discharge note',
              description: 'Item type for a discharge note in Care Timeline',
            })
          : isGeneralNote
            ? intl.formatMessage({
                defaultMessage: 'General note',
                description: 'Item type for a general note in Care Timeline',
              })
            : intl.formatMessage({
                defaultMessage: 'Care referral',
                description: 'Item type for a care referral in Care Timeline',
              }),
        itemType:
          isGeneralNote || isDischargeNote ? CareTimelineItemType.GENERAL_NOTE : CareTimelineItemType.CARE_REFERRAL,
        providerId: authorProviderId,
        providerImageUrl: providerImageByProviderId.get(authorProviderId) || undefined,
        providerName,
        serviceType: serviceNameByEpisodeId[note.episode_id] || formatCareTimelineValue(episode?.program_name),
        hasNote: true,
        noteId: note.id,
        noteActivityDate: getNoteActivityDate(note),
        noteStatus: note.status,
        isCancelled: false,
      },
    ]
  })

  const providerNameByEpisodeId: Record<string, string> = {}
  sessions.forEach((session) => {
    const episode = episodeById.get(session.episodeId)
    if (episode && session.provider.id === episode.provider_id) {
      providerNameByEpisodeId[session.episodeId] = session.provider.displayName || session.provider.name
    }
  })

  const milestoneItems = episodes.flatMap<CareTimelineItem>((episode) => {
    const serviceType = serviceNameByEpisodeId[episode.id] || formatCareTimelineValue(episode.program_name)
    const providerName = providerNameByEpisodeId[episode.id] || '—'
    const episodeStartDate = episode.start_date ? parseISO(episode.start_date) : undefined
    const earliestAppointmentDate = earliestAttendedDateByEpisodeId.get(episode.id)
    const startDate =
      episodeStartDate && isValid(episodeStartDate)
        ? earliestAppointmentDate && earliestAppointmentDate < episodeStartDate
          ? earliestAppointmentDate
          : episodeStartDate
        : earliestAppointmentDate
    const intakeNote = intakeNoteByEpisodeId.get(episode.id)
    const milestones: CareTimelineItem[] = []

    if (startDate && isValid(startDate)) {
      milestones.push({
        id: `milestone-${episode.id}-care-started`,
        date: startDate,
        episodeId: episode.id,
        title: getCareTimelineMilestoneTitle(intl, CareTimelineMilestoneType.CARE_STARTED),
        detailType: intl.formatMessage({
          defaultMessage: 'Milestone',
          description: 'Item type for a milestone in Care Timeline',
        }),
        itemType: CareTimelineItemType.MILESTONE,
        milestoneType: CareTimelineMilestoneType.CARE_STARTED,
        programName: episode.program_name,
        providerId: episode.provider_id,
        providerName,
        serviceType,
        hasNote: Boolean(intakeNote),
        noteId: intakeNote?.id,
        isCancelled: false,
      })
    }

    const terminalMilestoneType = terminalMilestoneByEpisodeState[episode.state]
    if (terminalMilestoneType && episode.end_date) {
      const endDate = parseISO(episode.end_date)
      if (isValid(endDate)) {
        milestones.push({
          id: `milestone-${episode.id}-${terminalMilestoneType}`,
          date: endDate,
          episodeId: episode.id,
          title: getCareTimelineMilestoneTitle(intl, terminalMilestoneType),
          detailType: intl.formatMessage({
            defaultMessage: 'Milestone',
            description: 'Item type for a milestone in Care Timeline',
          }),
          itemType: CareTimelineItemType.MILESTONE,
          milestoneType: terminalMilestoneType,
          dischargeReason: episode.state,
          dischargeComment: episode.end_episode_note,
          programName: episode.program_name,
          providerId: episode.provider_id,
          providerName,
          serviceType,
          isCancelled: false,
        })
      }
    }

    return milestones
  })

  return [...sessionItems, ...noteItems, ...milestoneItems]
}

export const getCareTimelineCustomDateKey = (date: string): string | undefined => {
  if (!date) {
    return undefined
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(date) && isValid(parseISO(date))) {
    return date
  }

  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(date)
  if (!match) {
    return undefined
  }

  const [, month, day, year] = match
  const paddedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  return isValid(parseISO(paddedDate)) ? paddedDate : undefined
}

const getCareTimelineCustomDate = (date: string): Date | undefined => {
  const dateKey = getCareTimelineCustomDateKey(date)
  return dateKey ? parseISO(dateKey) : undefined
}

type GetCareTimelineDateRangeBoundsArgs = {
  customDateFrom: string
  customDateTo: string
}

export const getCareTimelineDateRangeBounds = ({
  customDateFrom,
  customDateTo,
}: GetCareTimelineDateRangeBoundsArgs): { fromMaxDate?: Date; toMinDate?: Date } => ({
  fromMaxDate: getCareTimelineCustomDate(customDateTo),
  toMinDate: getCareTimelineCustomDate(customDateFrom),
})

export const isCareTimelineDateWithinBounds = (
  date: string,
  { minDate, maxDate }: { minDate?: Date; maxDate?: Date },
): boolean => {
  if (!date) {
    return true
  }

  const parsedDate = getCareTimelineCustomDate(date)
  return Boolean(
    parsedDate &&
    (!minDate || !isBefore(parsedDate, startOfDay(minDate))) &&
    (!maxDate || !isAfter(parsedDate, startOfDay(maxDate))),
  )
}

const isWithinDateFilter = (
  itemDate: Date,
  filters: CareTimelineFilters,
  currentDate: Date,
  timeZone: string,
): boolean => {
  const { date: dateFilter } = filters
  if (dateFilter === CareTimelineDateFilter.ANY) {
    return true
  }

  if (dateFilter === CareTimelineDateFilter.CUSTOM_RANGE) {
    const itemDateKey = formatInTimeZone(itemDate, timeZone, 'yyyy-MM-dd')
    const fromDateKey = getCareTimelineCustomDateKey(filters.customDateFrom)
    const toDateKey = getCareTimelineCustomDateKey(filters.customDateTo)

    return (!fromDateKey || itemDateKey >= fromDateKey) && (!toDateKey || itemDateKey <= toDateKey)
  }

  const startDate = {
    [CareTimelineDateFilter.PAST_WEEK]: subDays(currentDate, 7),
    [CareTimelineDateFilter.PAST_MONTH]: subMonths(currentDate, 1),
    [CareTimelineDateFilter.PAST_YEAR]: subYears(currentDate, 1),
  }[dateFilter]

  return itemDate >= startDate && itemDate <= currentDate
}

const itemTypeByFilter: Record<CareTimelineTypeFilter, CareTimelineItemType> = {
  [CareTimelineTypeFilter.CARE_START_STOP]: CareTimelineItemType.MILESTONE,
  [CareTimelineTypeFilter.GENERAL_NOTES]: CareTimelineItemType.GENERAL_NOTE,
  [CareTimelineTypeFilter.REFERRALS]: CareTimelineItemType.CARE_REFERRAL,
  [CareTimelineTypeFilter.SESSIONS]: CareTimelineItemType.SESSION,
}

export const filterAndSortCareTimelineItems = (
  items: CareTimelineItem[],
  filters: CareTimelineFilters,
  currentDate = new Date(),
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
): CareTimelineItem[] =>
  items
    .filter((item) => filters.service === 'all' || filters.service.includes(item.serviceType))
    .filter((item) => filters.provider === 'all' || filters.provider.includes(item.providerId || item.providerName))
    .filter((item) => !item.attendance || filters.attendance.includes(item.attendance))
    .filter((item) => filters.type.some((type) => itemTypeByFilter[type] === item.itemType))
    .filter((item) => isWithinDateFilter(item.date, filters, currentDate, timeZone))
    .sort((a, b) => {
      const direction = filters.sort === CareTimelineSort.NEWEST ? -1 : 1
      const dateComparison = a.date.getTime() - b.date.getTime()
      if (dateComparison !== 0) {
        return dateComparison * direction
      }

      const aChronologicalOrder = a.milestoneType === CareTimelineMilestoneType.CARE_STARTED ? 0 : 1
      const bChronologicalOrder = b.milestoneType === CareTimelineMilestoneType.CARE_STARTED ? 0 : 1
      return (aChronologicalOrder - bChronologicalOrder) * direction
    })

const isSameDayInTimeZone = (firstDate: Date, secondDate: Date, timeZone: string): boolean =>
  formatInTimeZone(firstDate, timeZone, 'yyyy-MM-dd') === formatInTimeZone(secondDate, timeZone, 'yyyy-MM-dd')

export const getCareTimelineDisplayRows = (items: CareTimelineItem[], timeZone: string): CareTimelineDisplayRow[] =>
  items.map((item, index) => {
    const previousItem = items[index - 1]
    const nextItem = items[index + 1]
    const isMilestone = item.itemType === CareTimelineItemType.MILESTONE

    return {
      item,
      showDate:
        !isMilestone &&
        (!previousItem ||
          previousItem.itemType === CareTimelineItemType.MILESTONE ||
          !isSameDayInTimeZone(previousItem.date, item.date, timeZone)),
      hasFollowingItemOnSameDay:
        !isMilestone &&
        Boolean(
          nextItem &&
          nextItem.itemType !== CareTimelineItemType.MILESTONE &&
          isSameDayInTimeZone(item.date, nextItem.date, timeZone),
        ),
    }
  })
